import { parseGS1Barcode, GS1Data } from '../../../../../utils/gs1Parser';

export interface BarcodeProcessResult {
    itemNo: string;
    quantity?: number;
    batchNo?: string;
    expiryDate?: string;
    sscc?: string;
    isGS1: boolean;
}

/**
 * Process a barcode input and extract item information.
 * Handles both GS1-128 structured barcodes and plain item numbers.
 *
 * @param scanInput - The raw barcode string from scanner
 * @returns Processed barcode data with item number and optional GS1 fields
 */
export function processBarcodeInput(scanInput: string): BarcodeProcessResult | null {
    if (!scanInput || !scanInput.trim()) return null;

    console.log('Processing barcode input:', scanInput);

    // Remove trailing scanner separators to check for real GS1 structure
    const withoutTrailingSeparators = scanInput.replace(/[~§]+$/g, '').trim();

    // Check if this looks like a GS1-128 structured barcode
    // GS1-128 has separators WITHIN the string (not just trailing), or ]C1 prefix, or parentheses
    const hasInternalSeparators = withoutTrailingSeparators.includes('~') ||
                                   withoutTrailingSeparators.includes('§') ||
                                   withoutTrailingSeparators.includes(String.fromCharCode(29));
    const hasParentheses = scanInput.includes('(');
    const hasC1Prefix = scanInput.startsWith(']C1');

    // A real GS1 barcode has structure indicators, not just trailing separators from scanner
    const looksLikeGS1 = hasInternalSeparators || hasParentheses || hasC1Prefix;

    let gs1Data: GS1Data | null = null;
    let hasGS1Data = false;

    if (looksLikeGS1) {
        console.log('GS1 structure detected, attempting GS1 parse...');
        // Try parsing as GS1-128 using the robust parser
        gs1Data = parseGS1Barcode(scanInput);
        console.log('GS1 parse result:', gs1Data);

        // Check if we got any GS1 data
        hasGS1Data = !!(gs1Data.parsedAIs && Object.keys(gs1Data.parsedAIs).length > 0);
        console.log('GS1 parse result - hasGS1Data:', hasGS1Data, 'parsedAIs:', gs1Data?.parsedAIs);
    } else {
        console.log('No GS1 structure detected, treating as plain barcode');
    }

    if (hasGS1Data && gs1Data && gs1Data.gtin) {
        // GS1-128 barcode with GTIN
        console.log('GS1-128 format detected');
        return {
            itemNo: gs1Data.gtin,
            quantity: gs1Data.quantity,
            batchNo: gs1Data.batchNo,
            expiryDate: gs1Data.expiryDate || gs1Data.bestBeforeDate || gs1Data.useByDate,
            sscc: gs1Data.sscc,
            isGS1: true
        };
    } else {
        // Plain barcode (just item number)
        console.log('Plain barcode format detected');
        // Strip trailing scanner separators (~ and §) from plain barcodes
        const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
        console.log('Cleaned plain barcode:', cleanedItemNo);
        return {
            itemNo: cleanedItemNo,
            isGS1: false
        };
    }
}
